import React, { useState } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { Quran01Icon } from 'hugeicons-react';
import DateTimeCard from './DateTimeCard';
import PrayerTimesCard from './PrayerTimesCard';
import DailyDuaCard from './DailyDuaCard';
import DailyHadithCard from './DailyHadithCard';
import QuranProgressCard from './QuranProgressCard';
import AdhkarTrackerCard from './AdhkarTrackerCard';
import MobileGoalsCard from './MobileGoalsCard';
import QuranGuestCard from './QuranGuestCard';
import { useSettings } from '../../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { SURAHS } from '../../constants/quranConstants';
import { AnimatePresence, motion } from 'framer-motion';

const formatLastRead = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
  return `${Math.floor(diff/86400000)}d ago`;
};

const LastReadCTA = ({ isDarkMode, lastSession, surahs, onClick, textColor = "white" }) => {
  // Use provided surahs, or fallback to SURAHS constant if missing/empty
  const surahList = surahs && surahs.length > 0 ? surahs : SURAHS.map(s => ({
    id: s.number.toString(),
    name_simple: s.name
  }));
  const surah = surahList?.find(s => s.id.toString() === lastSession?.surahId);
  
  return (
    <div 
      onClick={onClick}
      className="relative cursor-pointer"
    >
      <div className="relative z-10 py-4 flex justify-between items-center">
        <div className="flex flex-col h-full justify-between">
          <h2 className={`text-lg font-medium mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {lastSession?.surahId ? 'Continue Reading' : 'Start Reading'}
          </h2>
          
          {/* Reading information aligned in one line */}
          <div className="flex items-center gap-3">
            {lastSession?.surahId ? (
              <>
                <p className={`text-base font-medium ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                  {surah?.name_simple}
                </p>
                <span className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-gray-600'}`}>
                  • Verse {lastSession.verseId}
                </span>
                <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
                  {formatLastRead(lastSession.timestamp)}
                </span>
              </>
            ) : (
              <p className={`text-base ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>
                Begin your Quran journey
              </p>
            )}
          </div>
        </div>
        
        {/* Resume button centered vertically */}
        <button className={`px-3 py-1.5 rounded-full text-sm ${
          isDarkMode 
            ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-800/50' 
            : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
        }`}>
          {lastSession?.surahId ? 'Resume' : 'Start'}
        </button>
      </div>
    </div>
  );
};

const MobileLayout = ({ 
  currentUser, 
  isDarkMode, 
  dashboardData, 
  greeting, 
  setDashboardData,
  isLoading,
  isInitialized,
  location,
  locationError
}) => {
  const { settings, isInitialized: settingsInitialized } = useSettings();
  const navigate = useNavigate();
  const [showGuestProgress, setShowGuestProgress] = React.useState(() => {
    // If localStorage says to show progress, do so
    return localStorage.getItem('showQuranProgressAsGuest') === 'true';
  });

  // Add an effect to monitor changes to the dua and hadith indices
  React.useEffect(() => {
    console.log('[MobileLayout] dashboardData indices changed:', {
      currentDuaIndex: dashboardData?.currentDuaIndex,
      currentHadithIndex: dashboardData?.currentHadithIndex
    });
  }, [dashboardData?.currentDuaIndex, dashboardData?.currentHadithIndex]);

  const handleToggleAdhkar = (prayer) => {
    setDashboardData(prevData => ({
      ...prevData,
      adhkarCompleted: {
        ...prevData.adhkarCompleted,
        [prayer]: !prevData.adhkarCompleted[prayer]
      }
    }));
  };

  const handleNextDua = () => {
    console.log('[MobileLayout] handleNextDua called', {
      currentIndex: dashboardData?.currentDuaIndex,
      duasLength: dashboardData?.duas?.length
    });
    
    if (!dashboardData?.duas?.length) {
      console.warn('[MobileLayout] Cannot navigate - no duas available');
      return;
    }
    
    // Create a completely new object to ensure the reference changes and forces a re-render
    const newDashboardData = {
      ...dashboardData,
      currentDuaIndex: (dashboardData.currentDuaIndex + 1) % dashboardData.duas.length
    };
    
    console.log('[MobileLayout] Setting new dua index:', newDashboardData.currentDuaIndex);
    setDashboardData(newDashboardData);
  };

  const handlePreviousDua = () => {
    console.log('[MobileLayout] handlePreviousDua called', {
      currentIndex: dashboardData?.currentDuaIndex,
      duasLength: dashboardData?.duas?.length
    });
    
    if (!dashboardData?.duas?.length) {
      console.warn('[MobileLayout] Cannot navigate - no duas available');
      return;
    }
    
    // Create a completely new object to ensure the reference changes and forces a re-render
    const newDashboardData = {
      ...dashboardData,
      currentDuaIndex: (dashboardData.currentDuaIndex - 1 + dashboardData.duas.length) % dashboardData.duas.length
    };
    
    console.log('[MobileLayout] Setting new dua index:', newDashboardData.currentDuaIndex);
    setDashboardData(newDashboardData);
  };

  const handleNextHadith = () => {
    console.log('[MobileLayout] handleNextHadith called', {
      currentIndex: dashboardData?.currentHadithIndex,
      hadithsLength: dashboardData?.hadiths?.length
    });
    
    if (!dashboardData?.hadiths?.length) {
      console.warn('[MobileLayout] Cannot navigate - no hadiths available');
      return;
    }
    
    // Create a completely new object to ensure the reference changes and forces a re-render
    const newDashboardData = {
      ...dashboardData,
      currentHadithIndex: (dashboardData.currentHadithIndex + 1) % dashboardData.hadiths.length
    };
    
    console.log('[MobileLayout] Setting new hadith index:', newDashboardData.currentHadithIndex);
    setDashboardData(newDashboardData);
  };

  const handlePreviousHadith = () => {
    console.log('[MobileLayout] handlePreviousHadith called', {
      currentIndex: dashboardData?.currentHadithIndex,
      hadithsLength: dashboardData?.hadiths?.length
    });
    
    if (!dashboardData?.hadiths?.length) {
      console.warn('[MobileLayout] Cannot navigate - no hadiths available');
      return;
    }
    
    // Create a completely new object to ensure the reference changes and forces a re-render
    const newDashboardData = {
      ...dashboardData,
      currentHadithIndex: (dashboardData.currentHadithIndex - 1 + dashboardData.hadiths.length) % dashboardData.hadiths.length
    };
    
    console.log('[MobileLayout] Setting new hadith index:', newDashboardData.currentHadithIndex);
    setDashboardData(newDashboardData);
  };

  const handleGuestNoThanks = () => {
    localStorage.setItem('showQuranProgressAsGuest', 'true');
    setShowGuestProgress(true);
  };

  // Function to get the welcome message
  const getWelcomeMessage = () => {
    if (!currentUser) return "Welcome, Guest";
    
    // If user has set a display name, use it
    if (currentUser.displayName) {
      return `Welcome, ${currentUser.displayName}`;
    }
    
    // If no display name is set, just show "Welcome"
    return "Welcome";
  };

  return (
    <div className={`flex flex-col min-h-full overscroll-none ${isDarkMode ? 'bg-[#111111]' : ''}`}>
      {/* Header section - Always render since it doesn't depend on async data */}
      <div className="relative px-4 pb-20 overflow-hidden">
        <div className="absolute inset-0 top-[-100px] bottom-[-100px]">
          <div className="absolute inset-0 bg-black"></div>
          <img
            src="/images/bgpattern.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b dark:from-[#042f21]/95 dark:via-[#042f21]/30.5 dark:to-[#042f21]/60 from-emerald-600/95 via-emerald-600/30.5 to-emerald-600/60"></div>
        </div>
        <div className="relative z-10 w-full pt-6">
          <h1 className="text-lg font-light mb-1 text-white" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
            {getWelcomeMessage()}    ٱلسَّلَامُ عَلَيْكمْ
          </h1>
          <p className="text-sm mb-12 text-white/80">
            {greeting}
          </p>
          {settings?.dashboardItems?.dateTime && settings?.coordinates && settings?.location && (
            <DateTimeCard 
              dateTime={dashboardData?.dateTime} 
              prayerTimes={dashboardData?.prayerTimes}
              isLoading={
                !dashboardData?.dateTime ||
                !dashboardData?.prayerTimes ||
                !dashboardData?.prayerTimes.times ||
                Object.keys(dashboardData.prayerTimes.times || {}).length === 0
              }
              className="space-y-1"
              timeClassName="text-4xl font-medium text-white"
              untilClassName="text-md text-gray-300"
              dateClassName="text-sm text-white/80"
              dividerClassName="text-white/40"
            />
          )}
        </div>
      </div>
      
      {/* New wrapper solely for positioning and stacking context */}
      <div className="relative z-20">
        {/* Solid background layer - handles shape, bg, and clipping */}
        <div className={`-mt-16 rounded-[64px] overflow-hidden ${
          isDarkMode ? 'bg-transparent' : 'bg-gray-50'
        }`}>
          {/* Main content section - Simplified and flatter for dark mode, minimalist for light */}
          <div className={`relative h-full px-4 pt-8 pb-8 overflow-hidden rounded-[64px] ${
            isDarkMode
              ? 'bg-[#111111] border border-neutral-800'
              : 'bg-white shadow-lg'
          }`}>
            {/* Background elements - simplified for dark mode */}
            {isDarkMode ? (
              <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
                {/* Simplified background gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-neutral-900 via-neutral-900 to-neutral-900"></div>
                
                {/* Single subtle accent glow */}
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl bg-emerald-900/10"></div>
                
                {/* Very subtle edge highlight */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/5 to-transparent opacity-20"></div>
              </div>
            ) : (
              /* Light mode elegant styling */
              <div className="absolute inset-0">
                {/* Subtle background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50"></div>
                
                {/* Subtle emerald accent in corners */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-emerald-500/5 to-transparent rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-emerald-500/5 to-transparent rounded-tr-full"></div>
                
                {/* Soft shadow at the top */}
                <div className="absolute top-0 inset-x-0 h-10 bg-gradient-to-b from-gray-100/70 to-transparent"></div>
                
                {/* Enhanced border and shadow for better contrast */}
                <div className="absolute inset-0 rounded-[inherit] border border-gray-200 shadow-inner"></div>
                
                {/* Bottom edge enhancement for better separation */}
                <div className="absolute bottom-0 inset-x-0 h-8 bg-gradient-to-t from-gray-200/30 to-transparent"></div>
              </div>
            )}
            
            {/* Content container */}
            <div className="space-y-8 relative z-10">
              {/* Prayer Times Section */}
              {settings?.dashboardItems?.prayerTimes && (
                <div className="relative">
                  <div className="flex items-center justify-between mb-4 pl-4">
                    <h2 className={`text-lg font-medium ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      Prayer Times
                    </h2>
                    {settings?.location && (
                      <span className={`text-xs font-medium whitespace-nowrap ml-2 pr-10 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`} style={{ letterSpacing: '0.01em' }}>
                        {settings?.location}
                      </span>
                    )}
                  </div>
                  {/* Simplified Prayer Times container */}
                  <div className="relative">
                    {console.log('[MobileLayout] Prayer Times Props:', {
                      hasPrayerTimes: !!dashboardData?.prayerTimes,
                      hasPrayerTimesError: !!dashboardData?.prayerTimesError,
                      hasCoordinates: !!settings?.coordinates,
                      hasLocation: !!settings?.location,
                      prayerTimes: dashboardData?.prayerTimes
                    })}
                    <PrayerTimesCard 
                      prayerTimes={dashboardData?.prayerTimes} 
                      error={dashboardData?.prayerTimesError}
                      isLoading={
                        // ONLY show loading state if we have coordinates/location but are waiting for prayer times
                        settings?.coordinates && 
                        settings?.location && 
                        (!dashboardData?.prayerTimes || 
                         !dashboardData?.prayerTimes.times || 
                         Object.keys(dashboardData?.prayerTimes?.times || {}).length === 0)
                      }
                      hideTitle
                      style={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        border: 'none'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rest of the components - Show only when settings are initialized */}
      {!isInitialized ? (
        <div className="flex flex-col min-h-[300px] items-center justify-center">
          <div className="w-8 h-8 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mb-4" />
          <span className="text-gray-500 dark:text-gray-400 text-sm">Loading remaining content...</span>
        </div>
      ) : (
        <div className="relative overflow-hidden py-16 -mt-16 mb-8">
          {/* Main background gradient */}
          <div className={`absolute inset-0 ${
            isDarkMode
              ? 'bg-gradient-to-b from-[#111111] via-[#0c1f1a] to-[#111111]'
              : 'bg-gradient-to-b from-gray-100 via-emerald-100/70 to-gray-100'
          }`} />
          
          {/* Decorative gradient orbs */}
          <div className={`absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl -translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-900/20'
              : 'bg-emerald-400/30'
          }`} />
          <div className={`absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-900/15'
              : 'bg-emerald-300/40'
          }`} />
          
          {/* Additional dark mode decorative elements */}
          {isDarkMode && (
            <>
              <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950/10 via-transparent to-emerald-950/5" />
              
              {/* Adding a subtle pattern for more texture */}
              <div className="absolute inset-0 opacity-[0.03]" 
                   style={{
                     backgroundImage: 'url("/images/bgpattern.png")', 
                     backgroundSize: '400px',
                     mixBlendMode: 'luminosity'
                   }}>
              </div>
              
              {/* Extra dark bottom fill to prevent white showing through */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-[#111111]" style={{transform: 'translateY(100%)'}} />
            </>
          )}
          
          {/* Additional light mode decorative elements */}
          {!isDarkMode && (
            <>
              <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
              <div className="absolute inset-0 backdrop-blur-[80px]" />
              
              {/* Adding a subtle pattern for more texture */}
              <div className="absolute inset-0 opacity-[0.04]" 
                   style={{
                     backgroundImage: 'url("/images/bgpattern.png")', 
                     backgroundSize: '400px',
                     filter: 'invert(1)', 
                     mixBlendMode: 'multiply'
                   }}>
              </div>
            </>
          )}
          
          {/* Content container */}
          <div className="relative px-4">
            {/* LastReadCTA - Integrated into this section with minimal styling */}
            <div className="relative mb-1 px-4">
              <LastReadCTA 
                isDarkMode={isDarkMode} 
                lastSession={{
                  surahId: settings?.quran?.readingProgress?.currentSession?.surah,
                  verseId: settings?.quran?.readingProgress?.currentSession?.verse,
                  timestamp: settings?.quran?.readingProgress?.currentSession?.timestamp
                }}
                surahs={dashboardData?.surahs}
                onClick={() => navigate('/quran', { 
                  state: { 
                    surahId: settings?.quran?.readingProgress?.currentSession?.surah,
                    verseId: `${settings?.quran?.readingProgress?.currentSession?.surah}:${settings?.quran?.readingProgress?.currentSession?.verse}`
                  } 
                })}
              />
            </div>

            {/* Quran Progress Section - Now styled exactly like other cards */}
            {settings?.dashboardItems?.quranTracker && (
              <div className={`rounded-[24px] overflow-hidden px-4 ${
                isDarkMode 
                  ? 'bg-white/5 backdrop-blur-xl' 
                  : 'bg-white/60 backdrop-blur-xl'
              }`}>
                <div className="relative py-6">
                  <AnimatePresence mode="wait">
                    {currentUser ? (
                      <QuranProgressCard 
                        key="progress-logged-in"
                        isMobile={true} 
                        isLoading={isLoading} 
                        showToggle={true}
                        className={!isDarkMode ? 'light-mode-quran-progress' : ''}
                      />
                    ) : (
                      showGuestProgress ? (
                        <motion.div
                          key="progress-guest"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                        >
                          <QuranProgressCard 
                            isMobile={true} 
                            isLoading={isLoading} 
                            showToggle={true}
                            className={!isDarkMode ? 'light-mode-quran-progress' : ''}
                          />
                        </motion.div>
                      ) : (
                        <QuranGuestCard 
                          key="guest-cta"
                          isDarkMode={isDarkMode} 
                          showNoThanksButton={true}
                          onNoThanks={handleGuestNoThanks}
                        />
                      )
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
            
            {/* Container for remaining sections with space-y-8 */}
            <div className="space-y-8 mt-8">
              {/* Goals Section - Now below Quran Progress */}
              {settings?.dashboardItems?.goals && (
                <div className="relative">
                  <MobileGoalsCard isLoading={isLoading} />
                </div>
              )}
              
              {/* Daily Dua Section */}
              {settings?.dashboardItems?.dailyDua && (
                <>
                  {console.log('[MobileLayout] Rendering DailyDuaCard', {
                    currentIndex: dashboardData?.currentDuaIndex,
                    duasLength: dashboardData?.duas?.length,
                    hasDuas: !!dashboardData?.duas && dashboardData.duas.length > 0,
                    dua: dashboardData?.duas?.[dashboardData?.currentDuaIndex]
                  })}
                  <DailyDuaCard 
                    dailyDua={dashboardData?.duas?.[dashboardData?.currentDuaIndex]} 
                    onNext={handleNextDua}
                    onPrevious={handlePreviousDua}
                    isLoading={!dashboardData?.duas || dashboardData?.duas.length === 0}
                  />
                </>
              )}
              
              {/* Daily Hadith Section */}
              {settings?.dashboardItems?.dailyHadith && (
                <>
                  {console.log('[MobileLayout] Rendering DailyHadithCard', {
                    currentIndex: dashboardData?.currentHadithIndex,
                    hadithsLength: dashboardData?.hadiths?.length,
                    hasHadiths: !!dashboardData?.hadiths && dashboardData.hadiths.length > 0,
                    hadith: dashboardData?.hadiths?.[dashboardData?.currentHadithIndex]
                  })}
                  <DailyHadithCard 
                    dailyHadith={dashboardData?.hadiths?.[dashboardData?.currentHadithIndex]} 
                    onNext={handleNextHadith}
                    onPrevious={handlePreviousHadith}
                    isLoading={!dashboardData?.hadiths || dashboardData?.hadiths.length === 0}
                    error={dashboardData?.hadithsError}
                  />
                </>
              )}

              {/* Adhkar Tracker Section */}
              {settings?.dashboardItems?.adhkarTracker && (
                <div>
                  <AdhkarTrackerCard 
                    adhkarCompleted={dashboardData?.adhkarCompleted} 
                    onToggleAdhkar={handleToggleAdhkar} 
                    isLoading={!dashboardData?.adhkarCompleted}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileLayout; 